import { useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Money } from '@rental/shared';
import { api } from '../lib/api.js';
import { AttachmentField } from '../components/Attachment.jsx';
import { Badge, Button, Card, Empty, Field, Page, Table, TextInput } from '../components/ui.jsx';

interface Category { id: string; slug: string; name: string; kind: 'opex' | 'installation' | 'other'; }
interface Property { id: string; name: string; }
interface Expense {
  id: string;
  categorySlug: string;
  amount: number;
  spentOn: string;
  description: string | null;
  vendor: string | null;
  propertyId: string | null;
  unitId: string | null;
  attachmentId: string | null;
}

export function ExpensesPage() {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const now = new Date();
  const firstOfMonth = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}-01`;
  const [from, setFrom] = useState(firstOfMonth);
  const [to, setTo] = useState(now.toISOString().slice(0, 10));
  const [categorySlug, setCategorySlug] = useState('');

  const categoriesQ = useQuery({
    queryKey: ['expense-categories'],
    queryFn: () => api<{ categories: Category[] }>('/api/v1/expenses/categories'),
  });
  const propertiesQ = useQuery({
    queryKey: ['properties'],
    queryFn: () => api<{ properties: Property[] }>('/api/v1/properties'),
  });
  const expensesQ = useQuery({
    queryKey: ['expenses', from, to, categorySlug],
    queryFn: () =>
      api<{ expenses: Expense[]; totalPaise: number }>(
        `/api/v1/expenses?from=${from}&to=${to}${categorySlug ? `&categorySlug=${categorySlug}` : ''}`,
      ),
  });

  const catBySlug = useMemo(() => {
    const m = new Map<string, Category>();
    for (const c of categoriesQ.data?.categories ?? []) m.set(c.slug, c);
    return m;
  }, [categoriesQ.data]);
  const propBy = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of propertiesQ.data?.properties ?? []) m.set(p.id, p.name);
    return m;
  }, [propertiesQ.data]);

  return (
    <Page
      title="Expenses"
      actions={<Button onClick={() => setShowForm((v) => !v)}>{showForm ? 'Cancel' : '+ New expense'}</Button>}
    >
      <Card className="mb-4">
        <div className="grid gap-3 md:grid-cols-4 md:items-end">
          <Field label="From">
            <TextInput type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </Field>
          <Field label="To">
            <TextInput type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </Field>
          <Field label="Category">
            <select
              value={categorySlug} onChange={(e) => setCategorySlug(e.target.value)}
              className="w-full rounded border border-slate-300 bg-white px-3 py-2 dark:border-slate-600 dark:bg-slate-800"
            >
              <option value="">All</option>
              {(categoriesQ.data?.categories ?? []).map((c) => (
                <option key={c.slug} value={c.slug}>{c.name}</option>
              ))}
            </select>
          </Field>
          <div className="text-right">
            <div className="text-xs text-slate-500">Total</div>
            <div className="text-lg font-medium">
              {Money.fromPaise(expensesQ.data?.totalPaise ?? 0).format()}
            </div>
          </div>
        </div>
      </Card>

      {showForm && (
        <div className="mb-4">
          <ExpenseForm
            categories={categoriesQ.data?.categories ?? []}
            properties={propertiesQ.data?.properties ?? []}
            onSaved={() => {
              setShowForm(false);
              void qc.invalidateQueries({ queryKey: ['expenses'] });
            }}
          />
        </div>
      )}

      {expensesQ.isLoading ? (
        <Empty>Loading…</Empty>
      ) : !expensesQ.data?.expenses.length ? (
        <Empty>No expenses in this range.</Empty>
      ) : (
        <Table>
          <thead className="text-left text-slate-500">
            <tr>
              <th className="border-b px-3 py-2 dark:border-slate-700">Date</th>
              <th className="border-b px-3 py-2 dark:border-slate-700">Category</th>
              <th className="border-b px-3 py-2 dark:border-slate-700">Description</th>
              <th className="border-b px-3 py-2 dark:border-slate-700">Property</th>
              <th className="border-b px-3 py-2 dark:border-slate-700 text-right">Amount</th>
              <th className="border-b px-3 py-2 dark:border-slate-700">Attachment</th>
            </tr>
          </thead>
          <tbody>
            {expensesQ.data.expenses.map((e) => (
              <tr key={e.id} className="border-b border-slate-100 dark:border-slate-800">
                <td className="px-3 py-2 whitespace-nowrap text-slate-500">{e.spentOn}</td>
                <td className="px-3 py-2">
                  <Badge tone={catBySlug.get(e.categorySlug)?.kind === 'installation' ? 'warn' : 'neutral'}>
                    {catBySlug.get(e.categorySlug)?.name ?? e.categorySlug}
                  </Badge>
                </td>
                <td className="px-3 py-2">{e.description ?? '—'}</td>
                <td className="px-3 py-2 text-slate-500">
                  {e.propertyId ? propBy.get(e.propertyId) ?? '—' : '—'}
                </td>
                <td className="px-3 py-2 text-right">{Money.fromPaise(e.amount).format()}</td>
                <td className="px-3 py-2">
                  {e.attachmentId ? (
                    <a
                      className="text-xs underline"
                      href={`${import.meta.env.VITE_API_URL ?? 'http://localhost:8787'}/api/v1/attachments/${e.attachmentId}`}
                      target="_blank" rel="noreferrer"
                    >
                      View
                    </a>
                  ) : (
                    <span className="text-xs text-slate-400">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Page>
  );
}

function ExpenseForm({
  categories, properties, onSaved,
}: { categories: Category[]; properties: Property[]; onSaved: () => void }) {
  const [amount, setAmount] = useState('');
  const [spentOn, setSpentOn] = useState(new Date().toISOString().slice(0, 10));
  const [categorySlug, setCategorySlug] = useState(categories[0]?.slug ?? 'other');
  const [propertyId, setPropertyId] = useState('');
  const [description, setDescription] = useState('');
  const [vendor, setVendor] = useState('');
  const [attachmentId, setAttachmentId] = useState<string | null>(null);
  const [method, setMethod] = useState<'cash' | 'bank' | 'upi' | 'cheque' | 'other' | ''>('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api('/api/v1/expenses', { method: 'POST', body }),
    onSuccess: onSaved,
    onError: (e) => setError(e instanceof Error ? e.message : 'Save failed'),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const paise = Money.parse(amount).toPaise();
      mutation.mutate({
        amount: paise,
        spentOn,
        categorySlug,
        description: description || undefined,
        vendor: vendor || undefined,
        propertyId: propertyId || undefined,
        attachmentId: attachmentId || undefined,
        method: method || undefined,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid amount');
    }
  }

  return (
    <Card>
      <form onSubmit={onSubmit} className="grid gap-3 md:grid-cols-6 md:items-end">
        <Field label="Amount (₹)">
          <TextInput required value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="2500" />
        </Field>
        <Field label="Date">
          <TextInput type="date" required value={spentOn} onChange={(e) => setSpentOn(e.target.value)} />
        </Field>
        <Field label="Category">
          <select
            value={categorySlug} onChange={(e) => setCategorySlug(e.target.value)}
            className="w-full rounded border border-slate-300 bg-white px-3 py-2 dark:border-slate-600 dark:bg-slate-800"
          >
            {categories.map((c) => (<option key={c.slug} value={c.slug}>{c.name}</option>))}
          </select>
        </Field>
        <Field label="Property">
          <select
            value={propertyId} onChange={(e) => setPropertyId(e.target.value)}
            className="w-full rounded border border-slate-300 bg-white px-3 py-2 dark:border-slate-600 dark:bg-slate-800"
          >
            <option value="">— none —</option>
            {properties.map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
          </select>
        </Field>
        <Field label="Method">
          <select
            value={method} onChange={(e) => setMethod(e.target.value as typeof method)}
            className="w-full rounded border border-slate-300 bg-white px-3 py-2 dark:border-slate-600 dark:bg-slate-800"
          >
            <option value="">—</option>
            <option value="cash">Cash</option>
            <option value="bank">Bank</option>
            <option value="upi">UPI</option>
            <option value="cheque">Cheque</option>
            <option value="other">Other</option>
          </select>
        </Field>
        <Field label="Vendor">
          <TextInput value={vendor} onChange={(e) => setVendor(e.target.value)} />
        </Field>
        <div className="md:col-span-4">
          <Field label="Description">
            <TextInput value={description} onChange={(e) => setDescription(e.target.value)} placeholder="e.g. Bathroom pipe replacement" />
          </Field>
        </div>
        <div className="md:col-span-2 flex items-end">
          <AttachmentField entityType="expense" value={attachmentId} onChange={setAttachmentId} />
        </div>
        <div className="md:col-span-6 flex items-center justify-end gap-2">
          {error && <span className="text-xs text-red-600">{error}</span>}
          <Button type="submit" disabled={mutation.isPending}>Save expense</Button>
        </div>
      </form>
    </Card>
  );
}
